/**
 * Werkzeugzustand der Zeichenebene.
 *
 * Bewusst ein einziger, globaler Zustand: Werkzeug, Farbe und Strichstaerke
 * gelten in der ganzen App gleich, egal auf welchem Bildschirm man zeichnet.
 *
 * "active" ist der Schalter aus dem Auftrag: Werkzeug aus bedeutet, der Pencil
 * schreibt per iPadOS-Scribble in Textfelder. Werkzeug an bedeutet, der Pencil
 * zeichnet ueberall – auch ueber Textfeldern. Der Finger bedient in beiden
 * Faellen ganz normal die App.
 */
import type { DrawTool } from '../../types.ts';

export interface ToolState {
  active: boolean;
  tool: DrawTool;
  color: string;
  /** Strichstaerke in Pixeln bei normaler Schriftgroesse. */
  width: number;
  /** Deckkraft 0.1 bis 1. */
  opacity: number;
  /**
   * Zeichnet auch der Finger? Ohne Apple Pencil der einzige Weg. Dann
   * scrollt man mit zwei Fingern. Siehe fingerDefault().
   */
  finger: boolean;
  /** Hat die Person "Finger zeichnet" selbst umgestellt? Dann nicht mehr automatisch. */
  fingerChosen?: boolean;
}

const PEN_SEEN_KEY = 'leadsheet:pen-seen';

/**
 * Wurde auf diesem Geraet schon einmal mit einem Stift gezeichnet?
 * Solange nicht, zeichnet der Finger – sonst koennte man ohne Pencil gar
 * nicht zeichnen. Sobald ein Pencil auftaucht, scrollt der Finger wieder.
 */
function fingerDefault(): boolean {
  try {
    return localStorage.getItem(PEN_SEEN_KEY) !== 'yes';
  } catch {
    return true;
  }
}

/** Ein Stift wurde benutzt: ab jetzt scrollt der Finger (ausser selbst anders gewaehlt). */
export function notePenUsed(): void {
  try {
    if (localStorage.getItem(PEN_SEEN_KEY) === 'yes') return;
    localStorage.setItem(PEN_SEEN_KEY, 'yes');
  } catch {
    /* egal */
  }
  if (!state.fingerChosen && state.finger) setToolState({ finger: false });
}

export interface ColorDef {
  value: string;
  label: string;
}

/**
 * Feste Farbpalette, zwei Reihen zu acht.
 * Bewusst unabhaengig vom Theme: eine rote Anmerkung bleibt rot, egal ob die
 * App hell oder dunkel laeuft. Fuer alles andere gibt es den Farbwaehler,
 * der auf dem iPad das Farbrad von iPadOS oeffnet.
 */
export const PALETTE: ColorDef[] = [
  { value: '#1c1c1e', label: 'Schwarz' },
  { value: '#5b5f66', label: 'Grau' },
  { value: '#a9b0b8', label: 'Hellgrau' },
  { value: '#ffffff', label: 'Weiss' },
  { value: '#b8342a', label: 'Rot' },
  { value: '#d9682a', label: 'Orange' },
  { value: '#e0a52e', label: 'Gelb' },
  { value: '#5d8c3f', label: 'Grün' },
  { value: '#2c7a72', label: 'Petrol' },
  { value: '#2f5aa8', label: 'Blau' },
  { value: '#4a3f8f', label: 'Indigo' },
  { value: '#7b3f8f', label: 'Violett' },
  { value: '#c0507e', label: 'Rosa' },
  { value: '#8a5a3c', label: 'Braun' },
  { value: '#3f6b4f', label: 'Tanne' },
  { value: '#6b6a5e', label: 'Oliv' },
];

/** Fuenf Strichstaerken, wie in einer Notizen-App. */
export const WIDTHS = [
  { value: 1.5, label: 'Sehr fein' },
  { value: 2.5, label: 'Fein' },
  { value: 4, label: 'Mittel' },
  { value: 7, label: 'Kräftig' },
  { value: 12, label: 'Breit' },
];

/** Die Werkzeuge in der Reihenfolge der Palette. */
export const TOOLS: { value: DrawTool; label: string; hint: string }[] = [
  { value: 'pen', label: 'Stift', hint: 'Druckempfindlich, volle Deckkraft' },
  { value: 'marker', label: 'Marker', hint: 'Breit und durchscheinend' },
  { value: 'eraser', label: 'Radierer', hint: 'Radiert pixelgenau' },
];

const STORAGE_KEY = 'leadsheet:tool';

/** Startwerte, ergaenzt um das zuletzt Benutzte. */
function initialState(): ToolState {
  const base: ToolState = {
    active: false,
    tool: 'pen',
    color: PALETTE[0]?.value ?? '#1c1c1e',
    width: 4,
    opacity: 1,
    finger: fingerDefault(),
  };
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return base;
    const parsed = JSON.parse(stored) as Partial<ToolState>;
    // "active" wird nie wiederhergestellt: nach dem Start soll der Pencil
    // erst einmal wieder schreiben duerfen. "finger" nur, wenn selbst gewaehlt.
    const finger = parsed.fingerChosen ? Boolean(parsed.finger) : base.finger;
    return { ...base, ...parsed, finger, active: false };
  } catch {
    return base;
  }
}

const state: ToolState = initialState();

function remember(): void {
  try {
    const { tool, color, width, opacity, finger, fingerChosen } = state;
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ tool, color, width, opacity, finger, fingerChosen }));
  } catch {
    /* gesperrter Speicher ist kein Fehlerfall */
  }
}

type Listener = (state: ToolState) => void;
const listeners = new Set<Listener>();

export function getToolState(): ToolState {
  return { ...state };
}

export function setToolState(patch: Partial<ToolState>): void {
  Object.assign(state, patch);
  state.opacity = Math.min(1, Math.max(0.1, state.opacity));
  document.body.classList.toggle('drawing', state.active);
  document.body.classList.toggle('drawing--finger', state.active && state.finger);
  remember();
  for (const listener of listeners) listener(getToolState());
}

export function onToolChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Zeichnet diese Eingabe gerade? Stift bei aktivem Werkzeug immer, der
 * Finger nur mit "Mit dem Finger zeichnen". Fuer Flaechen, die sonst selbst
 * auf Zeiger reagieren (Verschieben der Band-Bibel-Leinwand).
 */
export function drawsWith(ev: { pointerType?: string }): boolean {
  if (!state.active) return false;
  return ev.pointerType === 'pen' || (ev.pointerType === 'touch' && state.finger);
}

/** Breite des Markers gegenueber dem Stift. */
export const MARKER_FACTOR = 3.2;
/** Grunddeckkraft des Markers, wird mit der eingestellten Deckkraft verrechnet. */
export const MARKER_ALPHA = 0.35;
